import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { apiFetch } from '../api';
import './AdminPanel.css';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatAge(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? '1 month' : `${months} months`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year' : `${years} years`;
}

/** Lightweight confirm modal for non-destructive admin actions
 *  (role grants/revokes). Uses the same admin-modal-* styles as
 *  DeleteConfirmModal so the destructive and reversible flows feel
 *  consistent. window.confirm is browser-styled and inconsistent
 *  with the rest of the app. */
function ConfirmModal({ title, message, confirmLabel, confirmVariant, onConfirm, onCancel, busy }) {
  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="admin-modal__actions">
          <button
            type="button"
            className="admin-btn admin-btn--cancel"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`admin-btn admin-btn--${confirmVariant || 'secondary'}`}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ user, onConfirm, onCancel, busy, error }) {
  const [typed, setTyped] = useState('');
  const canConfirm = typed === user.username && !busy;

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Delete user?</h3>
        <p>
          This will permanently delete <strong>{user.username}</strong> and
          all of their watchlists, portfolio holdings, and settings. This
          action cannot be undone.
        </p>
        <p className="admin-modal__instructions">
          Type <code>{user.username}</code> to confirm:
        </p>
        <input
          type="text"
          className="admin-modal__input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          spellCheck={false}
          autoComplete="off"
        />
        {error && <div className="admin-modal__error">{error}</div>}
        <div className="admin-modal__actions">
          <button
            type="button"
            className="admin-btn admin-btn--cancel"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--danger"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {busy ? 'Deleting…' : 'Delete user'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [roleBusy, setRoleBusy] = useState(null); // user_id while PATCH in-flight
  const [roleError, setRoleError] = useState(null);
  // Pending role change awaiting in-app confirmation. Null when no modal open.
  const [pendingRoleChange, setPendingRoleChange] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/users');
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load users');
      }
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleRoleChange(targetUserId, grantAdmin) {
    setRoleBusy(targetUserId);
    setRoleError(null);
    try {
      const res = await apiFetch(`/api/admin/users/${targetUserId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ is_admin: grantAdmin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      setUsers((prev) => prev.map((u) => (u.id === targetUserId ? data.user : u)));
      setFlash(data.message);
      setTimeout(() => setFlash(null), 4000);
    } catch (err) {
      setRoleError(err.message);
      setTimeout(() => setRoleError(null), 5000);
    } finally {
      setRoleBusy(null);
    }
  }

  async function handleConfirmDelete() {
    if (!targetUser) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await apiFetch(`/api/admin/users/${targetUser.id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }
      setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
      setFlash(data.message || `User ${targetUser.username} deleted`);
      setTargetUser(null);
      setTimeout(() => setFlash(null), 4000);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="admin-panel">
      <div className="admin-panel__header">
        <h2>Administration</h2>
        <p className="admin-panel__subtitle">
          Manage all registered users. Deleting a user cascades to their
          watchlists, portfolio, and settings.
        </p>
      </div>

      {flash && <div className="admin-flash admin-flash--success">{flash}</div>}
      {error && <div className="admin-flash admin-flash--error">{error}</div>}
      {roleError && <div className="admin-flash admin-flash--error">{roleError}</div>}

      {loading ? (
        <div className="admin-loading">Loading users…</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Member since</th>
                <th>Last login</th>
                <th>Email</th>
                <th className="admin-table__actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="admin-table__empty">No users found.</td>
                </tr>
              )}
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                const deletable = !isSelf && !u.is_admin;
                return (
                  <tr key={u.id}>
                    <td className="admin-table__username">
                      {u.username}
                      {isSelf && <span className="admin-table__self-tag"> (you)</span>}
                    </td>
                    <td>
                      {u.is_admin ? (
                        <span className="admin-badge admin-badge--admin">Admin</span>
                      ) : (
                        <span className="admin-badge">User</span>
                      )}
                    </td>
                    <td>
                      <div>{formatDate(u.created_at)}</div>
                      <div className="admin-table__age">{formatAge(u.created_at)}</div>
                    </td>
                    <td>{formatDate(u.last_login_at)}</td>
                    <td>{u.email || '—'}</td>
                    <td className="admin-table__actions-col">
                      {!isSelf && !u.is_admin && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--secondary admin-btn--small"
                          disabled={roleBusy === u.id}
                          title="Grant admin privileges"
                          onClick={() => setPendingRoleChange({ user: u, grant: true })}
                        >
                          {roleBusy === u.id ? 'Saving…' : 'Grant Admin'}
                        </button>
                      )}
                      {!isSelf && u.is_admin && (
                        <button
                          type="button"
                          className="admin-btn admin-btn--warning admin-btn--small"
                          disabled={roleBusy === u.id}
                          title="Revoke admin privileges"
                          onClick={() => setPendingRoleChange({ user: u, grant: false })}
                        >
                          {roleBusy === u.id ? 'Saving…' : 'Revoke Admin'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="admin-btn admin-btn--danger admin-btn--small"
                        disabled={!deletable}
                        title={
                          isSelf
                            ? 'You cannot delete your own account'
                            : u.is_admin
                              ? 'Cannot delete another admin'
                              : 'Delete user'
                        }
                        onClick={() => { setDeleteError(null); setTargetUser(u); }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {targetUser && (
        <DeleteConfirmModal
          user={targetUser}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (!deleteBusy) {
              setTargetUser(null);
              setDeleteError(null);
            }
          }}
          busy={deleteBusy}
          error={deleteError}
        />
      )}

      {pendingRoleChange && (
        <ConfirmModal
          title={pendingRoleChange.grant ? 'Grant admin privileges?' : 'Revoke admin privileges?'}
          message={
            pendingRoleChange.grant
              ? `${pendingRoleChange.user.username} will gain full admin access, including user management and API monitoring.`
              : `${pendingRoleChange.user.username} will lose admin access immediately on their next request.`
          }
          confirmLabel={pendingRoleChange.grant ? 'Grant Admin' : 'Revoke Admin'}
          confirmVariant={pendingRoleChange.grant ? 'secondary' : 'warning'}
          busy={roleBusy === pendingRoleChange.user.id}
          onConfirm={async () => {
            const { user: u, grant } = pendingRoleChange;
            await handleRoleChange(u.id, grant);
            setPendingRoleChange(null);
          }}
          onCancel={() => {
            if (roleBusy !== pendingRoleChange.user.id) {
              setPendingRoleChange(null);
            }
          }}
        />
      )}
    </div>
  );
}
